import { getDoctorCenters } from '@/common/apis/services/doctor/getDoctorCenters';
import { getPaymentMethods } from '@/common/apis/services/factor/paymentMethods';
import { getReviews } from '@/common/apis/services/reviews/getReviews';
import { useSearch } from '@/common/apis/services/search/search';
import Button from '@/common/components/atom/button';
import Skeleton from '@/common/components/atom/skeleton';
import Text from '@/common/components/atom/text';
import TextField from '@/common/components/atom/textField';
import ChatIcon from '@/common/components/icons/chat';
import QuotesIcon from '@/common/components/icons/quotes';
import { CENTERS } from '@/common/types/centers';
import buildSubstituteDoctorSearch from '@/common/utils/buildSubstituteDoctorSearch';
import classNames from '@/common/utils/classNames';
import { onlineVisitFactorUrl } from '@/common/utils/onlineVisitFactorUrl';
import { ALTERNATIVE_SORT, AlternativeMode, parseTomanText, pickAlternativeDoctors } from '@/modules/booking/utils/pickAlternativeDoctors';
import { getRateDetailsData, mapRateDetailsToCardRate } from '@/modules/profile/functions/getRateDetailsData';
import { paymentMethodsQuery } from '@/modules/booking/utils/retryPaymentMethod';
import { useQuery } from '@tanstack/react-query';
import { getCookie } from 'cookies-next';
import { useSearchStore } from '@/modules/search/store/search';
import { SUBSTITUTE_DOCTOR_SEARCH_REF } from '@/common/utils/buildSubstituteDoctorSearch';
import { buildInPersonLine } from '@/common/utils/substituteInPersonLine';
import ChevronIcon from '@/common/components/icons/chevron';
import OfficeIcon from '@/common/components/icons/office';
import LocationIcon from '@/common/components/icons/location';
import { useEffect, useRef } from 'react';
import Loading from '@/common/components/atom/loading/loading';
import ProductCard from '.plasmic/ProductCard';

export const CANCEL_REASONS = {
  inPerson: 'می‌خواهم حضوری مراجعه کنم',
  changeTime: 'می‌خواهم زمان دیگری بگیرم',
  price: 'هزینهٔ ویزیت زیاد است',
  slow: 'زمان پاسخ‌گویی دیر است',
  otherDoctor: 'پزشک دیگری می‌خواهم',
  bank: 'پرداخت بانکی مشکل دارد',
  other: 'دلیل دیگری دارم',
} as const;
export type CancelReason = keyof typeof CANCEL_REASONS;

export interface FollowUpProps {
  reason: CancelReason;
  slug: string;
  /** Online-visit service of the held turn (factor path), for re-picking its time. */
  serviceId?: string;
  /** Factor page expertises (getDoctorExpertise rows) — the alternatives search is built from them. */
  expertises?: any[];
  doctorName?: string;
  bookTime?: string;
  priceToman?: number;
  /** Held turn time (ms) — "faster" doctors must answer clearly before it. */
  turnAt?: number;
  previousPaymentMethod?: string;
  /** Selected payment method / written note, owned by the sheet (its footer acts on them). */
  paymentMethod?: string;
  onPaymentMethodChange: (method: string) => void;
  note: string;
  onNoteChange: (note: string) => void;
  /** Release the held book, then go there (another doctor / in-person booking). */
  onLeaveTo: (url: string, target: string) => void;
  busy: boolean;
  /** Pay the held book now (bank: on the chosen method). */
  onContinue: (paymentMethod?: string) => void;
  /** Release the held book (with the written reason). */
  onCancel: () => void;
  /** Record exactly what this reason's page offered (once, when its data is in) — offers are judged by it. */
  onOffer: (offer: Record<string, string | number | boolean | undefined>) => void;
}

/** Fire `onOffer` once, the first time `ready` is true. */
const useOfferOnce = (ready: boolean, onOffer: FollowUpProps['onOffer'], build: () => Parameters<FollowUpProps['onOffer']>[0]) => {
  const sent = useRef(false);
  useEffect(() => {
    if (!ready || sent.current) return;
    sent.current = true;
    onOffer(build());
  }, [ready]);
};

/** Same shape as the doctor card that replaces it, so nothing jumps when results land. */
const DoctorCardsSkeleton = () => (
  <div className="flex flex-col gap-2" aria-busy="true" aria-label="در حال یافتن پزشکان">
    <Skeleton w="12rem" h="1rem" rounded="md" />
    {[0, 1].map(i => (
      <div key={i} className="flex flex-col gap-3 p-4 rounded-xl border border-slate-200">
        <div className="flex items-center gap-3">
          <Skeleton w="72px" h="72px" rounded="full" />
          <div className="flex flex-col flex-1 gap-2">
            <Skeleton w="60%" h="1rem" rounded="md" />
            <Skeleton w="40%" h="0.8rem" rounded="md" />
            <Skeleton w="30%" h="0.8rem" rounded="md" />
          </div>
        </div>
        <Skeleton w="100%" h="44px" rounded="lg" />
      </div>
    ))}
  </div>
);

const PersuadeSkeleton = () => (
  <div className="flex flex-col gap-3 p-4 rounded-2xl bg-slate-50" aria-busy="true">
    <Skeleton w="80%" h="1rem" rounded="md" />
    <Skeleton w="35%" h="1.25rem" rounded="full" />
    <Skeleton w="100%" h="4rem" rounded="lg" />
    <Skeleton w="100%" h="4rem" rounded="lg" />
  </div>
);

const ChoiceRow = ({ icon: Icon, title, subtitle, onClick }: { icon: React.ComponentType<{ className?: string }>; title: string; subtitle?: string; onClick: () => void }) => (
  <button type="button" onClick={onClick} className="flex items-center gap-3 w-full min-h-[64px] px-4 py-3 text-right bg-white active:bg-slate-50">
    <Icon className="w-6 h-6 shrink-0 text-primary" />
    <div className="flex flex-col flex-1 min-w-0 gap-0.5">
      <Text fontWeight="medium" className="line-clamp-1">
        {title}
      </Text>
      {subtitle && (
        <Text fontSize="xs" className="text-slate-600 line-clamp-1">
          {subtitle}
        </Text>
      )}
    </div>
    <ChevronIcon dir="left" className="shrink-0 text-slate-700" width="8" height="12" />
  </button>
);

/** Shown while the held turn is released and the next page opens. */
export const LeavingNotice = ({ text }: { text: string }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-10 text-center" aria-busy="true">
    <Loading className="fill-primary" width={32} height={32} />
    <Text fontSize="sm" fontWeight="medium" className="text-slate-700">
      {text}
    </Text>
  </div>
);

const Caption = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="sm" fontWeight="semiBold" className="text-slate-700">
    {children}
  </Text>
);

/** Same specialty, online visit, ranked for what the patient said was wrong. Shown with the search card. */
const DoctorAlternatives = ({ mode, slug, expertises, doctorName, priceToman, turnAt, busy, onLeaveTo, onContinue, onOffer }: { mode: AlternativeMode } & FollowUpProps) => {
  // The specialty page (/s/ir/<group>/), like the substitute popup's in-person line: the profile
  // title (alias_title) is often a long marketing sentence and searching it returns unrelated doctors.
  const group = expertises?.[0]?.groups?.[0];
  const sortBy = ALTERNATIVE_SORT[mode];
  const route = group?.en_slug ? `ir/${group.en_slug}` : 'ir';
  const base = buildSubstituteDoctorSearch(
    (expertises ?? []).map(item => ({ expertise_id: item?.expertise?.id, expertise_name: item?.expertise?.name, alias_title: item?.alias_title })),
    'ir',
  );
  const search = useSearch(
    {
      route,
      query: { turn_type: 'consult', ref: base.query.ref, ...(!group?.en_slug && { text: base.query.text }), ...(sortBy && { sortBy }) },
    },
    { enabled: !!expertises?.length },
  );
  // The held doctor's own listing price, when the specialty page doesn't carry them.
  const own = useSearch({ route: 'ir', query: { turn_type: 'consult', text: doctorName ?? '' } }, { enabled: mode === 'cheaper' && !priceToman && !!doctorName });
  const ownHit = (own.data?.search?.result ?? []).find((hit: any) => hit.slug === slug);
  const hits = [...(search.data?.search?.result ?? []), ...(ownHit ? [ownHit] : [])];
  const doctors = pickAlternativeDoctors(hits, { mode, currentSlug: slug, currentPriceToman: priceToman, currentTurnAt: turnAt });
  const listingUrl = group?.en_slug ? `/s/ir/${group.en_slug}/?turn_type=consult${sortBy ? `&sortBy=${sortBy}` : ''}` : undefined;
  // Never a dead end: with no better doctor, keeping this one is the main path; the whole
  // specialty (same order) and a plain cancel stay available either way.
  // slug|price|satisfaction|reviews|first online turn (epoch s), in the order shown.
  useOfferOnce(!expertises?.length || (!search.isFetching && !!search.data), onOffer, () => ({
    variant: doctors.length ? 'doctors' : 'no_better',
    mode,
    offered_count: doctors.length,
    offered: doctors.map(d => [d.slug, parseTomanText(d.price), d.satisfaction, d.rates_count, d.consult_freeturn].join('|')).join(','),
    current_price: priceToman,
    current_turn_at: turnAt ? Math.round(turnAt / 1000) : undefined,
    pool: search.data?.search?.result?.length ?? 0,
    listing: !!listingUrl,
  }));
  const listingLabel = { cheaper: 'مشاهدهٔ پزشکان ارزان‌تر', faster: 'مشاهدهٔ پزشکان با نوبت زودتر', other: 'مشاهدهٔ پزشکان بیشتر' }[mode];
  const moreActions = (found: boolean) => (
    <div className="flex flex-col gap-2">
      {!found && (
        <Button block onClick={() => onContinue()} loading={busy}>
          ادامهٔ پرداخت همین نوبت
        </Button>
      )}
      {listingUrl && (
        <Button block variant="secondary" onClick={() => onLeaveTo(listingUrl, 'listing')} disabled={busy}>
          {listingLabel}
        </Button>
      )}
    </div>
  );
  if (expertises?.length && search.isFetching && !search.data) return <DoctorCardsSkeleton />;
  if (!doctors.length) return moreActions(false);
  const caption = {
    cheaper: priceToman ? `هم‌تخصص‌های پرامتیاز، ارزان‌تر از ${Math.round(priceToman).toLocaleString('fa-IR')} تومان` : 'هم‌تخصص‌های پرامتیاز با هزینهٔ کمتر', faster: 'هم‌تخصص‌های پرامتیازی که زودتر پاسخ می‌دهند', other: 'هم‌تخصص‌های پرامتیاز ویزیت آنلاین' }[mode];
  return (
    <div className="flex flex-col gap-2">
      <Caption>{caption}</Caption>
      {doctors.map(doctor => (
        <div
          key={doctor.slug}
          className="rounded-xl border border-slate-200 overflow-hidden"
          // The card links straight to the doctor; release this doctor's held slot first.
          onClickCapture={event => {
            event.preventDefault();
            event.stopPropagation();
            onLeaveTo(onlineVisitFactorUrl(doctor), doctor.slug ?? '');
          }}
        >
          <ProductCard
            title={[doctor.prefix, doctor.title].filter(Boolean).join(' ')}
            subTitle={doctor.display_expertise}
            avatarSrc={doctor.image}
            avatarAltText={[doctor.prefix, doctor.title].filter(Boolean).join(' ')}
            avatarRingColor="blue"
            satisfactionPercent={doctor.satisfaction}
            rateCount={doctor.rates_count}
            price={doctor.price}
            badges={doctor.badges}
            actionButtons={doctor.actions?.filter((action: any) => action?.title === 'ویزیت آنلاین')}
            url={{ href: onlineVisitFactorUrl(doctor), title: doctor.title }}
          />
        </div>
      ))}
      {moreActions(true)}
    </div>
  );
};

/** Wants in-person: this doctor's own clinic if it books (receipt cancel modal's banner), else why the online visit is worth it. */
/** The patient's city as search keeps it (store, else the saved cookie); '-1' = «همه شهرها» = unknown. */
const usePatientCity = () => {
  const stored = useSearchStore(state => state.city);
  if (stored?.id && stored.id !== '-1') return stored;
  try {
    const saved = getCookie('new-city');
    const city = saved ? JSON.parse(String(saved)) : undefined;
    return city?.id && city.id !== '-1' ? city : undefined;
  } catch {
    return undefined;
  }
};

const InPersonFollowUp = ({ slug, doctorName, expertises, bookTime, busy, onContinue, onLeaveTo, onOffer }: FollowUpProps) => {
  const centers = useQuery(['pendingPaymentCenters', slug], () => getDoctorCenters(slug), { enabled: !!slug, retry: 1 });
  const clinic = centers.data?.find(center => center.id !== CENTERS.CONSULT && center.active_booking !== false);
  // getDoctorCenters already keeps only bookable centers (its onlyBookable default).
  const persuade = !!slug && centers.isSuccess && !clinic;
  const rate = useQuery(['factorDoctorRate', slug], () => getRateDetailsData({ slug }), { enabled: persuade });
  const reviews = useQuery(['pendingPaymentReviews', slug], () => getReviews({ slug, sort: 'default_order', center_id: CENTERS.CONSULT, limit: 6 }), {
    enabled: persuade,
  });
  const cardRate = mapRateDetailsToCardRate(rate.data);
  const goodReviews = ((reviews.data?.list ?? []) as any[])
    .filter(review => review?.description?.length > 20 && Number(review?.recommended) !== 0)
    .slice(0, 2);

  // Clinic in the patient's own city: straight into its booking flow (held online slot released first).
  // Another or unknown city: don't send them to book somewhere they may not reach — let them choose.
  const patientCity = usePatientCity();
  const clinicCity = clinic?.city;
  const sameCity = !!clinic && !!patientCity && !!clinicCity?.slug && clinicCity.slug === patientCity.en_slug;
  const clinicUrl = clinic ? `/booking/${slug}/?centerId=${clinic.id}` : '';
  const leaving = useRef(false);
  useEffect(() => {
    if (!sameCity || leaving.current) return;
    leaving.current = true;
    onLeaveTo(clinicUrl, 'in_person');
  }, [sameCity]);
  // Same specialty, in person, in the patient's city — the substitute popup's «حضوری در شهر شما» line.
  const group = expertises?.[0]?.groups?.[0];
  const localSearch = useSearch(
    { route: `${patientCity?.en_slug}/${group?.en_slug}`, query: { ref: SUBSTITUTE_DOCTOR_SEARCH_REF } },
    { enabled: !!clinic && !sameCity && !!patientCity && !!group?.en_slug },
  );
  const localLine = buildInPersonLine(localSearch.data?.search?.result, doctorName);

  const inPersonVariant = !clinic ? 'no_clinic' : sameCity ? 'same_city' : patientCity ? 'other_city' : 'unknown_city';
  useOfferOnce(
    centers.isSuccess && (!clinic ? !rate.isLoading && !reviews.isLoading : sameCity || !localSearch.isFetching),
    onOffer,
    () => ({
      variant: inPersonVariant,
      clinic_city: clinicCity?.slug,
      patient_city: patientCity?.en_slug,
      local_count: localLine?.count ?? 0,
      local_earliest: localLine?.earliest,
      rate: cardRate?.text,
      rate_count: cardRate?.count,
      reviews_shown: goodReviews.length,
      response_time_shown: !!bookTime,
    }),
  );
  if (sameCity) return <LeavingNotice text="در حال رفتن به نوبت‌گیری حضوری…" />;
  if (clinic)
    return (
      <div className="flex flex-col gap-4">
        <Text fontWeight="bold">
          مطب {doctorName} {clinicCity?.name ? `در ${clinicCity.name}` : ''} است.
        </Text>
        <div className="flex flex-col divide-y divide-slate-100 rounded-2xl border border-slate-200 overflow-hidden">
          <ChoiceRow icon={OfficeIcon} title={`نوبت حضوری ${clinicCity?.name ? `در ${clinicCity.name}` : 'در مطب'}`} subtitle={clinic.name} onClick={() => onLeaveTo(clinicUrl, 'in_person_other_city')} />
          {patientCity && group?.en_slug && (localSearch.isLoading ? (
            <div className="flex items-center gap-3 min-h-[64px] px-4">
              <Skeleton w="24px" h="24px" rounded="md" />
              <div className="flex flex-col flex-1 gap-2">
                <Skeleton w="70%" h="0.9rem" rounded="md" />
                <Skeleton w="45%" h="0.8rem" rounded="md" />
              </div>
            </div>
          ) : localLine ? (
            <ChoiceRow
              icon={LocationIcon}
              title={`پزشکان ${group.name ?? ''} در ${patientCity.name}`}
              subtitle={`${localLine.count.toLocaleString('fa-IR')} پزشک، اولین نوبت ${localLine.earliest}`}
              onClick={() => onLeaveTo(`/s/${patientCity.en_slug}/${group.en_slug}/`, 'in_person_local')}
            />
          ) : null)}
        </div>
        <div className="flex flex-col gap-3 p-4 rounded-2xl bg-slate-50">
          <Text fontSize="sm" fontWeight="semiBold">
            یا همین پزشک را بدون رفت‌وآمد، آنلاین ببینید.
          </Text>
          <Button block onClick={() => onContinue()} loading={busy}>
            ادامهٔ پرداخت
          </Button>
        </div>
      </div>
    );
  if (centers.isLoading || (persuade && (rate.isLoading || reviews.isLoading))) return <PersuadeSkeleton />;
  if (!cardRate && !goodReviews.length && !bookTime) return null;
  return (
    <div className="flex flex-col gap-3 p-4 rounded-2xl bg-slate-50">
      <Text fontSize="sm" fontWeight="bold">
        {doctorName} نوبت حضوری ندارد؛ ویزیت آنلاینش را بیماران این‌طور دیده‌اند:
      </Text>
      {cardRate && (
        <div className="flex items-center gap-1 text-xs">
          <span className="h-5 px-2 rounded-full bg-green-600 text-slate-50 font-medium flex items-center">{cardRate.text}</span>
          <span className="text-slate-600">رضایت ({cardRate.count.toLocaleString('fa-IR')} نظر)</span>
        </div>
      )}
      {bookTime && (
        <div className="flex items-center gap-2">
          <ChatIcon className="w-5 h-5 shrink-0 text-primary" />
          <Text fontSize="sm">
            پاسخ پزشک:{' '}
            <Text fontSize="sm" fontWeight="semiBold">
              {bookTime}
            </Text>
          </Text>
        </div>
      )}
      {goodReviews.map(review => (
        <div key={review.Id} className="flex gap-2 p-3 rounded-xl bg-white">
          <QuotesIcon className="w-4 h-4 shrink-0 text-slate-400" />
          <Text fontSize="xs" className="text-slate-700 leading-6 line-clamp-3">
            {review.description}
          </Text>
        </div>
      ))}
      <Button block onClick={() => onContinue()} loading={busy}>
        ادامهٔ پرداخت
      </Button>
    </div>
  );
};

/** Bank trouble: the other methods this center offers, and what went wrong. */
const BankFollowUp = ({ priceToman, previousPaymentMethod, paymentMethod, onPaymentMethodChange, note, onNoteChange, busy, onContinue, onOffer }: FollowUpProps) => {
  const methods = useQuery(['pendingPaymentMethods', priceToman], () => getPaymentMethods(paymentMethodsQuery(CENTERS.CONSULT, priceToman ? priceToman * 10 : undefined)), {
    retry: 1,
  });
  const options = (methods.data?.data?.data?.payment_methods ?? []).filter(m => m.payment_method !== previousPaymentMethod);
  useOfferOnce(!methods.isLoading, onOffer, () => ({ variant: options.length ? 'methods' : 'no_methods', offered: options.map(m => m.payment_method).join(','), offered_count: options.length }));
  return (
    <div className="flex flex-col gap-3">
      {methods.isLoading ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map(i => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200">
              <Skeleton w="16px" h="16px" rounded="full" />
              <Skeleton w="28px" h="28px" rounded="md" />
              <Skeleton w="7rem" h="0.9rem" rounded="md" />
            </div>
          ))}
        </div>
      ) : (
        !!options.length && (
          <div className="flex flex-col gap-2">
            <Caption>با روش دیگری پرداخت کنید</Caption>
            {options.map(item => (
              <button
                key={item.payment_method}
                type="button"
                onClick={() => onPaymentMethodChange(item.payment_method)}
                className={classNames(
                  'flex items-center gap-3 px-4 py-3 text-right rounded-xl border transition-colors',
                  paymentMethod === item.payment_method ? 'border-primary bg-primary/5' : 'border-slate-200',
                )}
              >
                <span
                  className={classNames(
                    'w-4 h-4 shrink-0 rounded-full border-2',
                    paymentMethod === item.payment_method ? 'border-primary bg-primary shadow-[inset_0_0_0_2px_white]' : 'border-slate-300',
                  )}
                />
                {item.logo && <img src={item.logo} alt="" className="w-7 h-7 object-contain" />}
                <Text fontSize="sm" fontWeight="medium">
                  {item.title}
                </Text>
              </button>
            ))}
          </div>
        )
      )}
      <TextField multiLine label="چه مشکلی در پرداخت بانکی پیش آمد؟ (اختیاری)" value={note} onChange={e => onNoteChange(e.target.value)} />
      {paymentMethod && (
        <Button block onClick={() => onContinue(paymentMethod)} loading={busy}>
          پرداخت با روش انتخاب‌شده
        </Button>
      )}
    </div>
  );
};

const OtherFollowUp = ({ note, onNoteChange, busy, onCancel, onOffer }: FollowUpProps) => {
  useOfferOnce(true, onOffer, () => ({ variant: 'free_text' }));
  return (
  <div className="flex flex-col gap-3">
    <TextField multiLine label="دلیلتان را بنویسید" value={note} onChange={e => onNoteChange(e.target.value)} />
    <Button block variant="secondary" onClick={onCancel} disabled={!note.trim()} loading={busy}>
      ثبت و لغو نوبت
    </Button>
  </div>
  );
};

const ALTERNATIVE_MODES: Partial<Record<CancelReason, AlternativeMode>> = { price: 'cheaper', slow: 'faster', otherDoctor: 'other' };

/** What the sheet offers under the reason chips, for the reason the patient picked. */
/** Same doctor, another time: the online-visit booking flow with its time step (first real patient note, 2026-10-07). */
const ChangeTimeFollowUp = ({ slug, serviceId, doctorName, bookTime, busy, onLeaveTo, onOffer }: FollowUpProps) => {
  useOfferOnce(true, onOffer, () => ({ variant: 'change_time', service_id: serviceId, current_turn: bookTime }));
  return (
  <div className="flex flex-col gap-4">
    <Text fontSize="sm" className="text-slate-700 leading-6">
      نوبت فعلی {bookTime ? `(${bookTime}) ` : ''}آزاد می‌شود و می‌توانید زمان دیگری از {doctorName ?? 'همین پزشک'} انتخاب کنید.
    </Text>
    <Button
      block
      loading={busy}
      onClick={() => onLeaveTo(`/booking/${slug}/?centerId=${CENTERS.CONSULT}${serviceId ? `&serviceId=${serviceId}` : ''}`, 'change_time')}
    >
      انتخاب زمان دیگر
    </Button>
  </div>
  );
};

export const PendingPaymentFollowUp = (props: FollowUpProps) => {
  const mode = ALTERNATIVE_MODES[props.reason];
  if (props.reason === 'changeTime') return <ChangeTimeFollowUp {...props} />;
  if (mode) return <DoctorAlternatives mode={mode} {...props} />;
  if (props.reason === 'inPerson') return <InPersonFollowUp {...props} />;
  if (props.reason === 'bank') return <BankFollowUp {...props} />;
  return <OtherFollowUp {...props} />;
};
